import React from 'react';
import { Link } from "gatsby";
import SEO from "@/components/SEO";
import ServiceSchema from "@/components/ServiceSchema";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { BOOKING_URL, LINK_ATTRIBUTES } from "@/lib/config";
import {
    Sparkles,
    Star,
    Shield,
    Clock,
    CheckCircle,
    ArrowLeft,
    Phone,
    TriangleAlert,
    Building2
} from "lucide-react";


const TeethWhitening = () => {
    useScrollAnimation();

    const heroBenefits = [
        "Brighter appearance",
        "Convenient options",
        "Personalised care",
        "Safe & monitored"
    ];

    const benefits = [
        {
            title: "Brighter Appearance",
            description: "A lighter smile can enhance your overall look and confidence.",
            imageSrc: "/teeth-whitening.png"
        },
        {
            title: "Convenient Options",
            description: "Choose in-clinic treatments for faster results or take-home kits for flexibility.",
            imageSrc: "/options.png"
        },
        {
            title: "Personalised Care",
            description: "Treatments are customised to your preferences, from subtle brightening to more noticeable whitening.",
            imageSrc: "/dental-care.png"
        },
        {
            title: "Safe & Monitored",
            description: "Professional supervision helps manage sensitivity and ensure comfort.",
            imageSrc: "/healthcare.png"
        },
        {
            title: "Lasting Results",
            description: "Longevity varies between individuals as diet, lifestyle, and oral hygiene all play a role. With proper care and aftercare, results can often last for several months.",
            imageSrc: "/white-teeth.png"
        }
    ];

    return (
        <div className="min-h-screen flex flex-col">
      <SEO title="Professional Teeth Whitening Broadmeadows | Y3 Smiles Dental" description="Brighten your smile with professional teeth whitening at Y3 Smiles Dental in Broadmeadows. In-chair and take-home options available. Book a consultation online." image="/og-reception.jpg" />

      <ServiceSchema
        serviceName="Teeth Whitening"
        description={"Brighten your smile with professional teeth whitening at Y3 Smiles Dental in Broadmeadows. In-chair and take-home options available. Book a consultation online."}
        url="https://y3smilesdental.com.au/treatments/teeth-whitening/"
        priceFromAUD={450}
      />
            <Header />
            <main className="flex-grow">
                {/* Hero Section */}
                <section className="bg-gradient-hero py-24">
                              <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-5xl md:text-6xl font-bold text-neutral-800 mb-6">
                Teeth Whitening
              </h1>
                            <p className="text-xl text-neutral-800 mb-8 leading-relaxed">
                                At Y3 Smiles Dental, we believe every smile deserves to shine. Over time, coffee, tea, and natural ageing can leave your smile looking dull, dark, and discoloured. Professional teeth whitening is a safe way to refresh your smile and restore brightness.
                            </p>
                            <div className="flex flex-wrap justify-center gap-4 mb-8">
                                {heroBenefits.map((benefit, index) => (
                                    <div key={index} className="flex items-center bg-white/80 backdrop-blur-sm rounded-full px-4 py-2">
                                        <CheckCircle className="w-5 h-5 text-primary mr-2" />
                                        <span className="text-sm font-medium text-neutral-700">{benefit}</span>
                                    </div>
                                ))}
                            </div>
                            <Button variant="booking" size="xl" asChild>
                                <a href={BOOKING_URL} {...LINK_ATTRIBUTES}>BOOK CONSULTATION</a>
                            </Button>
                        </div>
                    </div>
                </section>

                {/* AHPRA Educational Introduction */}
                <section className="py-8 bg-white">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto">
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                                <p className="text-blue-800 text-sm leading-relaxed">
                                    Professional teeth whitening uses higher-concentration agents than over-the-counter products, applied under the supervision of a dental professional. Results vary depending on the natural shade of teeth, existing restorations, and the cause of discolouration. Your dentist can give you a realistic expectation of what professional whitening may achieve for your teeth. The information on this page is general education only.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* What is Teeth Whitening */}
                <section className="py-24 bg-background">
                    <div className="container mx-auto px-6">
                        <div className="max-w-6xl mx-auto">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                                <div>
                                    <h2 className="text-sm font-semibold text-primary uppercase tracking-widest mb-4">
                                        Professional Whitening
                                    </h2>
                                    <h3 className="text-4xl font-bold text-neutral-800 mb-6">
                                        What Is Teeth Whitening?
                                    </h3>
                                    <p className="text-lg text-neutral-800 mb-8 leading-relaxed">
                                        Teeth whitening is a cosmetic dental treatment that lightens tooth colour by reducing stains and discolouration. At Y3 Smiles Dental, we use regulated, high-grade materials and techniques that differ from over-the-counter kits, providing professional care tailored to your needs.
                                    </p>
                                    <div className="space-y-4">
                                        <div className="flex items-start">
                                            <CheckCircle className="w-6 h-6 text-primary mr-3 mt-1 flex-shrink-0" />
                                            <div>
                                                <h4 className="font-semibold text-neutral-800 mb-1">Professional Materials</h4>
                                                <p className="text-neutral-800 text-sm">Regulated, high-grade whitening materials for safe and effective results</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start">
                                            <CheckCircle className="w-6 h-6 text-primary mr-3 mt-1 flex-shrink-0" />
                                            <div>
                                                <h4 className="font-semibold text-neutral-800 mb-1">Tailored Treatment</h4>
                                                <p className="text-neutral-800 text-sm">Customised approach based on your individual needs and preferences</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start">
                                            <CheckCircle className="w-6 h-6 text-primary mr-3 mt-1 flex-shrink-0" />
                                            <div>
                                                <h4 className="font-semibold text-neutral-800 mb-1">Multiple Options</h4>
                                                <p className="text-neutral-800 text-sm">Choose between in-clinic treatment or custom take-home options</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="aspect-video bg-neutral-100 rounded-2xl overflow-hidden">
                                    <picture>
                                        <source type="image/webp" srcSet="/optimized/teeth-whitening-800w.webp 800w, /optimized/teeth-whitening-1600w.webp 1600w" sizes="(max-width: 1024px) 100vw, 640px" />
                                        <img
                                            src="/optimized/teeth-whitening.webp"
                                            alt="Professional teeth whitening results"
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                            decoding="async"
                                        />
                                    </picture>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Benefits of Teeth Whitening */}
                <section className="py-24 bg-neutral-50">
                    <div className="container mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl md:text-5xl font-bold text-neutral-800 mb-6">
                                Benefits of Teeth Whitening
                            </h2>
                            <p className="text-lg text-neutral-800 max-w-3xl mx-auto">
                                Discover how professional teeth whitening can enhance your smile and confidence.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {benefits.map((benefit, index) => {
                                return (
                                    <Card key={index} className="border-0 shadow-soft hover:shadow-medium hover:scale-[1.02] hover:-translate-y-1 transition-smooth fade-in-section" style={{ transitionDelay: `${index * 150}ms` }}>
                                        <CardHeader>
                                            <div className="w-16 h-16 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-4">
                                                <img src={benefit.imageSrc} alt={benefit.title} className="w-8 h-8" />
                                            </div>
                                            <CardTitle className="text-xl font-bold text-neutral-800 mb-2">
                                                {benefit.title}
                                            </CardTitle>
                                            <p className="text-neutral-800 leading-relaxed text-sm">
                                                {benefit.description}
                                            </p>
                                        </CardHeader>
                                    </Card>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* Treatment Options */}
                <section className="py-24 bg-background">
                    <div className="container mx-auto px-6">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl md:text-5xl font-bold text-neutral-800 mb-6">
                                Treatment Options
                            </h2>
                            <p className="text-lg text-neutral-800 max-w-3xl mx-auto">
                                Choose the teeth whitening option that best fits your lifestyle and preferences.
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                            <Card className="border-0 shadow-soft hover:shadow-medium hover:scale-[1.02] hover:-translate-y-1 transition-smooth">
                                <CardHeader className="text-center">
                                    <div className="w-20 h-20 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-6 mx-auto">
                                        <Building2 className="w-10 h-10 text-primary" />
                                    </div>
                                    <CardTitle className="text-2xl font-bold text-neutral-800 mb-4">
                                        In-Clinic Treatment
                                    </CardTitle>
                                    <p className="text-neutral-800 leading-relaxed mb-6">
                                        Professional whitening treatment performed in our clinic for faster, more dramatic results in a single visit.
                                    </p>
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-center">
                                            <CheckCircle className="w-5 h-5 text-primary mr-2" />
                                            <span className="text-sm text-neutral-800">Immediate results</span>
                                        </div>
                                        <div className="flex items-center justify-center">
                                            <CheckCircle className="w-5 h-5 text-primary mr-2" />
                                            <span className="text-sm text-neutral-800">Professional supervision</span>
                                        </div>
                                        <div className="flex items-center justify-center">
                                            <CheckCircle className="w-5 h-5 text-primary mr-2" />
                                            <span className="text-sm text-neutral-800">Single appointment</span>
                                        </div>
                                    </div>
                                </CardHeader>
                            </Card>

                            <Card className="border-0 shadow-soft hover:shadow-medium hover:scale-[1.02] hover:-translate-y-1 transition-smooth">
                                <CardHeader className="text-center">
                                    <div className="w-20 h-20 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-6 mx-auto">
                                        <img src="/dental-home.png" alt="Take-Home Kit" className="w-10 h-10" />
                                    </div>
                                    <CardTitle className="text-2xl font-bold text-neutral-800 mb-4">
                                        Take-Home Kit
                                    </CardTitle>
                                    <p className="text-neutral-800 leading-relaxed mb-6">
                                        Custom-fitted trays and professional-grade whitening gel for convenient whitening at your own pace.
                                    </p>
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-center">
                                            <CheckCircle className="w-5 h-5 text-primary mr-2" />
                                            <span className="text-sm text-neutral-800">Flexible timing</span>
                                        </div>
                                        <div className="flex items-center justify-center">
                                            <CheckCircle className="w-5 h-5 text-primary mr-2" />
                                            <span className="text-sm text-neutral-800">Custom-fitted trays</span>
                                        </div>
                                        <div className="flex items-center justify-center">
                                            <CheckCircle className="w-5 h-5 text-primary mr-2" />
                                            <span className="text-sm text-neutral-800">Gradual whitening</span>
                                        </div>
                                    </div>
                                </CardHeader>
                            </Card>
                        </div>
                    </div>
                </section>

                {/* Is Teeth Whitening Right For Me? */}
                <section className="py-16 bg-white">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-6">
                                Is Teeth Whitening Right for Me?
                            </h2>
                            <p className="text-lg text-neutral-800 mb-6 leading-relaxed">
                                Whitening is generally more effective on natural teeth. It may not change the colour of existing dental work such as crowns, veneers, or fillings, which means existing restorations may appear a different shade after treatment. If you have sensitive teeth or gum concerns, discuss this with your dentist before proceeding. A proper assessment is recommended to determine whether whitening is appropriate for you.
                            </p>
                        </div>
                    </div>
                </section>

                {/* FAQ Section */}
                <section className="py-16 bg-neutral-50">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto">
                            <div className="text-center mb-12">
                                <h2 className="text-4xl md:text-5xl font-bold text-neutral-800 mb-6">
                                    Frequently Asked Questions
                                </h2>
                            </div>
                            <Accordion type="single" className="w-full space-y-4">
                                <AccordionItem value="faq-0" className="bg-white rounded-lg shadow-soft border-0">
                                    <AccordionTrigger className="px-6 py-4 text-left hover:no-underline">
                                        <span className="text-lg font-semibold text-neutral-800">How long does professional teeth whitening last?</span>
                                    </AccordionTrigger>
                                    <AccordionContent className="px-6 pb-4">
                                        <p className="text-neutral-800 leading-relaxed">Results vary between individuals and depend on diet, lifestyle habits such as coffee or red wine consumption, and oral hygiene. Your dentist can advise on maintenance options to help prolong results.</p>
                                    </AccordionContent>
                                </AccordionItem>
                                <AccordionItem value="faq-1" className="bg-white rounded-lg shadow-soft border-0">
                                    <AccordionTrigger className="px-6 py-4 text-left hover:no-underline">
                                        <span className="text-lg font-semibold text-neutral-800">Is teeth whitening safe?</span>
                                    </AccordionTrigger>
                                    <AccordionContent className="px-6 pb-4">
                                        <p className="text-neutral-800 leading-relaxed">Professional teeth whitening performed under dental supervision is considered safe for most patients. Some people experience temporary sensitivity during or after treatment. Your dentist will assess your suitability before recommending a whitening option.</p>
                                    </AccordionContent>
                                </AccordionItem>
                                <AccordionItem value="faq-2" className="bg-white rounded-lg shadow-soft border-0">
                                    <AccordionTrigger className="px-6 py-4 text-left hover:no-underline">
                                        <span className="text-lg font-semibold text-neutral-800">Can I whiten my teeth if I have fillings or crowns?</span>
                                    </AccordionTrigger>
                                    <AccordionContent className="px-6 pb-4">
                                        <p className="text-neutral-800 leading-relaxed">Whitening agents work on natural tooth enamel and will not change the colour of existing restorations such as fillings, crowns, or veneers. Your dentist can discuss whether whitening is appropriate given your existing dental work.</p>
                                    </AccordionContent>
                                </AccordionItem>
                            </Accordion>
                        </div>
                    </div>
                </section>

                {/* Internal Linking Block */}
                <section className="py-12 bg-background">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto">
                            <h3 className="text-xl font-bold text-neutral-800 mb-4">Explore our services:</h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {[
                                    { label: "Children's Dentistry", to: "/treatments/childrens-dentistry" },
                                    { label: "Emergency Dentist", to: "/treatments/emergency" },
                                    { label: "Dental Fillings", to: "/treatments/dental-fillings" },
                                    { label: "Root Canal Treatment", to: "/treatments/root-canal" },
                                    { label: "Crowns and Bridges", to: "/treatments/crown-bridge" },
                                    { label: "Dental Implants", to: "/treatments/dental-implants" },
                                    { label: "Wisdom Teeth Removal", to: "/treatments/wisdom-teeth" },
                                    { label: "All Treatments", to: "/treatments" },
                                ].map((link) => (
                                    <Link key={link.to} to={link.to} className="text-sm text-primary hover:underline font-medium py-1">
                                        {link.label}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Disclaimer */}
                <section className="py-16 bg-neutral-50">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto">
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                                <h3 className="text-lg font-semibold text-yellow-800 mb-2 flex items-center">
                                    <TriangleAlert className="w-5 h-5 mr-2" />
                                    Disclaimer
                                </h3>
                                <p className="text-yellow-700 text-sm leading-relaxed">
                                    Teeth whitening is a cosmetic dental procedure and may not be suitable for everyone. All treatments carry potential risks. Please consult with your treating dentist to assess suitability, discuss risks, and understand recovery considerations before proceeding.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="py-24 bg-gradient-to-r from-primary/10 via-white to-secondary/10">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto text-center">
                            <h2 className="text-4xl font-bold text-neutral-800 mb-6">
                                Ready to Brighten Your Smile?
                            </h2>
                            <p className="text-xl text-neutral-800 mb-8 max-w-2xl mx-auto">
                                Discover the difference professional teeth whitening can make for your confidence and smile.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Button variant="booking" size="xl" asChild className="group">
                                    <a href={BOOKING_URL} {...LINK_ATTRIBUTES}>
                                        <Phone className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform" />
                                        BOOK ONLINE
                                    </a>
                                </Button>
                                <Button variant="outline" size="xl" asChild>
                                    <Link to="/treatments">View All Treatments</Link>
                                </Button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* <Contact /> */}
            </main>
            <Footer />
        </div>
    );
};

export default TeethWhitening;
